/**
 * BerichtenInline — embed op WI-detail-pagina's (Voorinspectie/Reinigen/Na-inspectie)
 * en WO-detail. Toont open berichten voor de scope, met inklap-thread, reageren,
 * foto-upload en afhandelen. "Bericht aan kantoor" opent de composer.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  BerichtError,
  fetchBerichtList,
  type BerichtSummary,
} from '../../lib/berichten'
import { BerichtComposer } from './BerichtComposer'
import { BerichtConversation } from './BerichtThread'

interface Props {
  scope: 'wo' | 'wi'
  werkOrderId: string
  werkOrderName?: string | null
  customerName?: string | null
  wasserijItemId?: string | null
  wasserijItemName?: string | null
}

export function BerichtenInline({
  scope,
  werkOrderId,
  werkOrderName,
  customerName,
  wasserijItemId,
  wasserijItemName,
}: Props) {
  const [berichten, setBerichten] = useState<BerichtSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [openThreadId, setOpenThreadId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showComposer, setShowComposer] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await fetchBerichtList(
        scope === 'wi'
          ? { werkOrderId, wasserijItemId: wasserijItemId ?? undefined, status: 'open' }
          : { werkOrderId, status: 'open' }
      )
      setBerichten(rows)
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Berichten laden mislukt')
    } finally {
      setLoading(false)
    }
  }, [scope, werkOrderId, wasserijItemId])

  useEffect(() => {
    refresh()
  }, [refresh])

  if (loading && berichten.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-kleda-border p-md">
        <p className="text-sm text-kleda-text-muted">Berichten laden…</p>
      </div>
    )
  }

  const hasItems = berichten.length > 0

  return (
    <div className="bg-white rounded-lg border border-kleda-border p-md space-y-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-kleda-text-main text-sm">
          Berichten
          {hasItems && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              {berichten.length}
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={() => setShowComposer(true)}
          className="text-sm font-medium text-kleda-primary hover:underline"
        >
          + Bericht aan kantoor
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>
      )}

      {!hasItems && !error && (
        <p className="text-xs text-kleda-text-muted italic">
          Geen openstaande berichten voor dit {scope === 'wo' ? 'order' : 'item'}.
        </p>
      )}

      <ul className="space-y-1">
        {berichten.map((bericht) => (
          <BerichtRow
            key={bericht.id}
            bericht={bericht}
            isOpen={openThreadId === bericht.id}
            onToggle={() => setOpenThreadId((cur) => (cur === bericht.id ? null : bericht.id))}
            onChanged={refresh}
          />
        ))}
      </ul>

      <BerichtComposer
        isOpen={showComposer}
        onClose={() => setShowComposer(false)}
        onSent={refresh}
        werkOrder={{ id: werkOrderId, name: werkOrderName ?? null, customerName: customerName ?? null }}
        wasserijItem={
          scope === 'wi' && wasserijItemId
            ? { id: wasserijItemId, name: wasserijItemName ?? null }
            : undefined
        }
      />
    </div>
  )
}

interface RowProps {
  bericht: BerichtSummary
  isOpen: boolean
  onToggle: () => void
  onChanged: () => void
}

function BerichtRow({ bericht, isOpen, onToggle, onChanged }: RowProps) {
  const fromOffice = bericht.richting === 'office_to_field'
  const accent = fromOffice ? 'border-amber-300 bg-amber-50/50' : 'border-kleda-border bg-white'
  return (
    <li className={`border rounded ${accent}`}>
      <button type="button" onClick={onToggle} className="w-full text-left px-2 py-2 flex items-start gap-2">
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-sm font-medium text-kleda-text-main truncate">
              {bericht.onderwerp || bericht.laatsteReactiePreview || '(geen onderwerp)'}
            </span>
            {bericht.prioriteit === 'urgent' && (
              <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700">
                Urgent
              </span>
            )}
          </div>
          <div className="text-[11px] text-kleda-text-muted flex items-center gap-1 flex-wrap">
            {bericht.wasserijItemName && <span>{bericht.wasserijItemName}</span>}
            <span>· {bericht.aantalReacties} bericht{bericht.aantalReacties === 1 ? '' : 'en'}</span>
            {fromOffice && <span className="text-amber-700">· Van kantoor</span>}
          </div>
        </div>
        <span className="text-kleda-text-muted text-xs mt-1">{isOpen ? '▴' : '▾'}</span>
      </button>
      {isOpen && <BerichtConversation berichtId={bericht.id} onChanged={onChanged} />}
    </li>
  )
}
